/**
 * WebTransport transport for OPBridge - the offline-capable data path.
 *
 * WHY: at a venue with no internet, `https://<hex>.bridge.overpoweredapps.net`
 * stops resolving ~2 min after the uplink dies (120 s TTL) and the whole rig
 * goes with it. WebTransport with a **pinned self-signed certificate** dials a
 * raw LAN IP, so it needs no DNS, no CA install and no router configuration -
 * nothing the user has to do. Proven on a real iPad (8th gen, iPadOS 26.6.1)
 * on 2026-08-31 with the WAN physically unplugged: shell from the service
 * worker cache, then a live session to the LAN. See `OFFLINE-LAN-RESEARCH.md`.
 *
 * WIRE CONTRACT (v1) - mirrored by `tools/wt-probe/src/bin/wt-gateway.rs`,
 * which is the reference implementation OPBridge should match:
 *
 *   one bidirectional stream PER REQUEST - the stream is the correlation, so
 *   there are no request ids and no multiplexing bookkeeping (QUIC streams are
 *   cheap, and this removes a whole class of state bug)
 *
 *   request  = u32 BE length + JSON { method, path, bearer|null, body|null }
 *   response = u32 BE length + JSON { status, body }   // body is a STRING
 *
 * `body` stays a string on the wire so neither side re-encodes a payload it
 * does not own - the same discipline that keeps a DNS forwarder correct.
 *
 * CONSTRAINTS worth knowing before changing this:
 *  - **Streams only.** WebKit ships WebTransport WITHOUT datagrams
 *    (`wt.datagrams` is undefined on 26.6.1). No datagram fast path on iPad.
 *  - **ECDSA P-256 only** and cert validity <= 14 days, per the pinning spec.
 *    RSA is rejected with a generic error that reads like "unsupported".
 *  - **Safari 26.4+.** Older devices cannot use this at all - which is why the
 *    OPBridge DNS-responder ask stays open as the path for them.
 */

/** Everything needed to dial one bridge over WebTransport. */
export interface WtTarget {
  /** e.g. `https://192.168.50.51:4433` - an IP, deliberately: no DNS. */
  url: string;
  /**
   * SHA-256 pins for the server's leaf cert, 64 hex chars each. **An array,
   * and that is the whole trick behind surviving a long offline gap.**
   *
   * A pinned cert may not exceed 14 days (WebKit enforces it), so a single
   * pin goes stale fast - and worse, the daemon re-mints ~3 days before
   * expiry, so a cached pin actually dies around day 10 when the bridge
   * starts presenting the NEXT cert. A device that had not connected since
   * then would arrive at a venue holding a hash for a cert nobody serves.
   *
   * `serverCertificateHashes` natively accepts MANY hashes and matches
   * whichever the server presents. So the cap applies per CERTIFICATE, not
   * to how many we may hold: the bridge pre-mints a chain of future certs
   * and publishes all their hashes, we cache the set, and the browser sorts
   * out which one is live. No date arithmetic, no retry loop, no "which is
   * current" logic on our side.
   */
  certHashesHex: string[];
}

export interface WtResponse {
  status: number;
  body: string;
}

/**
 * A transport-level failure, tagged with WHERE it happened. The tag is the
 * whole point and it is a safety property, not diagnostics:
 *
 *  - `dial`   - the session never opened, so **nothing was written to the
 *               wire**. Retrying the same request over `fetch` cannot
 *               duplicate a side effect, because there was no first attempt.
 *  - `stream` - the request bytes were already sent. Whether the daemon
 *               acted on them is UNKNOWABLE from here, so this must never be
 *               auto-retried: re-sending a `POST /midi/send` that actually
 *               landed fires the note twice, on stage.
 *
 * `httpClient` falls back to `fetch` on `dial` only, and surfaces `stream`
 * to the caller exactly the way a mid-flight `fetch` failure already is.
 */
export class WtTransportError extends Error {
  constructor(
    public readonly phase: 'dial' | 'stream',
    message: string,
  ) {
    super(message);
    this.name = 'WtTransportError';
  }
}

/** How long to wait for `wt.ready`. QUIC's own handshake timeout is far
 *  longer than anything useful here - an unreachable LAN endpoint would
 *  otherwise stall each request for ~10s before the fetch fallback got a
 *  turn, which on a gig reads as a dead rig rather than a slow one. */
const DIAL_TIMEOUT_MS = 2_000;

/** After a failed dial, stop trying for this long. Without it, EVERY request
 *  pays the full dial timeout while the endpoint is down - turning a working
 *  fetch path into a 2s-per-call crawl. One dial per window is enough to
 *  notice the endpoint coming back. */
const DIAL_COOLDOWN_MS = 15_000;

/** Returns a Uint8Array explicitly backed by an ArrayBuffer (not
 *  SharedArrayBuffer), which is what `BufferSource` requires. */
function hexToBytes(hex: string): Uint8Array<ArrayBuffer> {
  const clean = hex.trim().toLowerCase().replace(/[^0-9a-f]/g, '');
  if (clean.length !== 64) throw new Error(`cert hash must be 64 hex chars, got ${clean.length}`);
  const out = new Uint8Array(new ArrayBuffer(32));
  for (let i = 0; i < 32; i++) out[i] = parseInt(clean.slice(i * 2, i * 2 + 2), 16);
  return out;
}

/** True when this browser can do pinned-cert WebTransport at all. Callers use
 *  it to decide whether the offline path is even available, and to say so
 *  honestly rather than failing mysteriously on an older iPad. */
export function wtSupported(): boolean {
  return typeof globalThis !== 'undefined' && 'WebTransport' in globalThis;
}

/**
 * A pooled WebTransport session to one bridge. One session, many streams.
 *
 * Deliberately NOT auto-reconnecting: the caller owns retry policy, and a
 * transport that silently reconnects underneath a failing request is how you
 * get a green check on a dead link - the exact failure this project keeps
 * hitting.
 */
export class WtConnection {
  private wt: WebTransport | null = null;
  private connecting: Promise<WebTransport> | null = null;
  /** Epoch ms before which no dial is attempted. See `DIAL_COOLDOWN_MS`. */
  private coolDownUntilMs = 0;
  /** Every completed request, so the UI can say the offline path is LIVE
   *  rather than merely configured - "Connect must not lie" (DEC-186). */
  private _served = 0;
  private _lastErr: string | null = null;

  constructor(private readonly target: WtTarget) {}

  /** Where this session dials. Exposed for diagnostics copy. */
  url(): string { return this.target.url; }
  /** How many pins this session can offer. Diagnostics: 1 means the daemon
   *  publishes no chain yet, so the offline window is the ~10 days until it
   *  re-mints, not months. */
  pinCount(): number { return this.target.certHashesHex.length; }
  /** Requests this session has actually answered. 0 means the path has never
   *  once worked, which must never be displayed as "offline path ready". */
  servedCount(): number { return this._served; }
  lastError(): string | null { return this._lastErr; }
  /** True while a session is open right now (not merely dialable). */
  isOpen(): boolean { return this.wt !== null; }

  /**
   * Whether a dial is worth attempting at all right now. Callers check this
   * to skip straight to `fetch` during a cooldown instead of paying the dial
   * timeout on every single request.
   */
  usable(): boolean {
    if (!wtSupported()) return false;
    return Date.now() >= this.coolDownUntilMs;
  }

  /** Open (or reuse) the session. Concurrent callers share one dial.
   *
   *  `budgetMs` lets a caller with a tighter deadline than `DIAL_TIMEOUT_MS`
   *  impose it. The connection manager's `/health` probe is the case that
   *  matters: it runs on a 1200ms budget sized against a 5s bridge-loss
   *  alert deadline, and a dial allowed to outlast that would push confirmed-
   *  offline past the window the whole polling schedule is built around. */
  private async session(budgetMs = DIAL_TIMEOUT_MS): Promise<WebTransport> {
    if (this.wt) return this.wt;
    if (this.connecting) return this.connecting;
    const dialMs = Math.max(1, Math.min(DIAL_TIMEOUT_MS, budgetMs));

    this.connecting = (async () => {
      if (!wtSupported()) throw new Error('WebTransport unavailable (needs Safari 26.4+)');
      const pins = this.target.certHashesHex;
      if (pins.length === 0) throw new Error('no certificate pins to dial with');
      const wt = new WebTransport(this.target.url, {
        // Every pin we hold, handed over at once. The browser matches the one
        // the bridge is actually presenting; the rest are inert.
        serverCertificateHashes: pins.map((hex) => ({
          algorithm: 'sha-256' as const,
          value: hexToBytes(hex),
        })),
        // Required with serverCertificateHashes - pooling would reuse a
        // connection whose cert was validated by a different rule.
        allowPooling: false,
      });
      // Drop the cached session as soon as it dies, so the NEXT request
      // redials instead of writing into a closed transport.
      void wt.closed.catch(() => undefined).finally(() => {
        if (this.wt === wt) this.wt = null;
      });
      // Bound the handshake. `wt.ready` has no timeout of its own, and an
      // endpoint that is simply absent leaves it pending far longer than a
      // caller can wait. On timeout the transport is closed rather than left
      // dangling, or a late handshake would resurrect a session nobody holds.
      let timer: ReturnType<typeof setTimeout> | undefined;
      try {
        await Promise.race([
          wt.ready,
          new Promise<never>((_, rej) => {
            timer = setTimeout(
              () => rej(new Error(`dial timed out after ${dialMs}ms`)),
              dialMs,
            );
          }),
        ]);
      } catch (err) {
        try { wt.close(); } catch { /* never opened */ }
        throw err;
      } finally {
        if (timer !== undefined) clearTimeout(timer);
      }
      this.wt = wt;
      return wt;
    })();

    try {
      const wt = await this.connecting;
      this.coolDownUntilMs = 0;
      return wt;
    } catch (err) {
      // Back off before the next attempt, and tag the phase so the caller
      // knows nothing reached the wire and a fetch retry is safe.
      this.coolDownUntilMs = Date.now() + DIAL_COOLDOWN_MS;
      const msg = err instanceof Error ? err.message : String(err);
      this._lastErr = msg;
      throw new WtTransportError('dial', msg);
    } finally {
      this.connecting = null;
    }
  }

  /** Issue one request on its own stream. */
  async request(
    method: string,
    path: string,
    opts: { bearer?: string | null; body?: unknown; timeoutMs?: number; headers?: Record<string, string> } = {},
  ): Promise<WtResponse> {
    const timeoutMs = opts.timeoutMs ?? 8000;
    // `session()` throws a phase-'dial' error, which is the safe-to-retry one.
    const wt = await this.session(timeoutMs);
    // ONE deadline for the whole request, armed BEFORE the stream opens
    // (audit 2026-11-03 B-3). It used to start at the read, so a session whose
    // peer had vanished - QUIC keeps it open until its idle timeout - could
    // hang the stream open or a write with no limit at all, and a health tick
    // stuck there never reported the computer gone: it read "connected". On
    // the deadline the session is closed too: a stalled one would stall the
    // next request the same way.
    let timer: ReturnType<typeof setTimeout> | undefined;
    const deadline = new Promise<never>((_, rej) => {
      timer = setTimeout(() => {
        if (this.wt === wt) { try { wt.close(); } catch { /* already closed */ } this.wt = null; }
        rej(new Error(`WebTransport request timed out after ${timeoutMs}ms`));
      }, timeoutMs);
    });
    deadline.catch(() => undefined); // raced below; never an unhandled rejection
    // Opening a stream on an established session can still fail if the
    // session died between the two. Nothing has been written yet, so this is
    // still 'dial' - the retry-safety property is about bytes on the wire,
    // not about which call threw.
    let stream: WebTransportBidirectionalStream;
    try {
      stream = await Promise.race([wt.createBidirectionalStream(), deadline]);
    } catch (err) {
      if (timer !== undefined) clearTimeout(timer);
      if (this.wt === wt) this.wt = null;
      const msg = err instanceof Error ? err.message : String(err);
      this._lastErr = msg;
      this.coolDownUntilMs = Date.now() + DIAL_COOLDOWN_MS;
      throw new WtTransportError('dial', `stream open failed: ${msg}`);
    }

    const frame = JSON.stringify({
      method,
      path,
      bearer: opts.bearer ?? null,
      body: opts.body ?? null,
      // A request's own headers (the edit lease holder), for a door that passes them on (2026-11-04). OPBridge's frame
      // today is method, path, bearer, body; it ignores a field it does not know.
      ...(opts.headers ? { headers: opts.headers } : {}),
    });
    const payload = new TextEncoder().encode(frame);
    const header = new Uint8Array(4);
    new DataView(header.buffer).setUint32(0, payload.length, false);

    // From here on the request is ON THE WIRE, so every failure is phase
    // 'stream' and must NOT be auto-retried - see WtTransportError.
    try {
      const writer = stream.writable.getWriter();
      await Promise.race([writer.write(header), deadline]);
      await Promise.race([writer.write(payload), deadline]);
      // Tolerate a rejection here. The request bytes are already on the wire, so
      // a peer that stops reading its receive side (STOP_SENDING) must not fail
      // a request the server went on to handle correctly - a real observed
      // failure mode, not a theoretical one.
      await Promise.race([writer.close().catch(() => undefined), deadline]);

      const reader = stream.readable.getReader();
      const chunks: Uint8Array[] = [];
      let total = 0;

      // Read until the length prefix is satisfied. A single read() can return a
      // partial frame; assuming otherwise truncates under load and surfaces as a
      // JSON parse error nowhere near the cause.
      try {
        for (;;) {
          const { value, done } = await Promise.race([reader.read(), deadline]);
          if (value) { chunks.push(value); total += value.length; }
          if (total >= 4) {
            const head = concat(chunks, total);
            const want = new DataView(head.buffer, head.byteOffset, 4).getUint32(0, false);
            if (total >= 4 + want) {
              const json = new TextDecoder().decode(head.subarray(4, 4 + want));
              const res = JSON.parse(json) as WtResponse;
              this._served += 1;
              this._lastErr = null;
              return res;
            }
          }
          if (done) throw new Error('stream ended before a complete response frame');
        }
      } finally {
        try { reader.releaseLock(); } catch { /* already released */ }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      this._lastErr = msg;
      // Cool down on a mid-flight failure too, NOT just a failed dial.
      //
      // This protects the bridge-loss alert budget. When the daemon dies the
      // first casualty is an in-flight request on an already-open session -
      // a 'stream' failure, which leaves `usable()` true, so the NEXT health
      // tick would pay a full dial before falling back to fetch and only
      // then record its second failure. That pushes confirmed-offline past
      // the ~5s window `connection.ts`'s whole polling schedule is sized
      // for. Backing off here means the second tick goes straight to fetch
      // and the alert lands on its original timing.
      //
      // Cheap to be wrong about: a transient stream error costs 15s of fetch,
      // which is what the app did for its entire life before this shipped.
      this.coolDownUntilMs = Date.now() + DIAL_COOLDOWN_MS;
      throw new WtTransportError('stream', msg);
    } finally {
      // Without this the per-request deadline timer stays armed for its full
      // duration after a fast response, which on a 2s poll loop means a
      // permanently growing pile of pending timers on an iPad.
      if (timer !== undefined) clearTimeout(timer);
    }
  }

  close(): void {
    try { this.wt?.close(); } catch { /* already closed */ }
    this.wt = null;
  }
}

function concat(chunks: Uint8Array[], total: number): Uint8Array {
  if (chunks.length === 1) return chunks[0]!;
  const out = new Uint8Array(total);
  let off = 0;
  for (const c of chunks) { out.set(c, off); off += c.length; }
  return out;
}
